import { useState, useEffect, useMemo } from "react";
import { t } from "@/i18n";
import { useParams, useNavigate } from "react-router-dom";
import { CreatorBadge } from "@/components/CreatorBadge";
import { CreatorLevelBadge } from "@/components/CreatorLevelBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, MoreHorizontal, MessageCircle, Check, X, Send, Lock, Eye, Gift, Sparkles, UserMinus, Users, Play, Palmtree, Copy, Grid3X3, MapPin } from "lucide-react";
import { toast } from "sonner";
import { MentionSuggestions, applyMention } from "@/components/MentionSuggestions";
import { api } from "@/App";
import { getSceneImage } from "@/components/IslandScene";
import { GoldenRingAvatar } from "@/components/GoldenRingAvatar";
import { DiscussionGridTile } from "@/components/PostCard";
import { AdLikeIcon } from "@/components/AdeIcon";
import { GiftButton, GiftValueCounter, playConnectionSound } from "@/components/GiftSystem";
import { GiftDetailsModal } from "@/components/ProfileGiftDetails";
import { PhotoZoomDialog } from "@/components/PhotoZoom";
import { AdeLoadingSpinner } from "@/components/AdeLoadingSpinner";

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userDiscussions, setUserDiscussions] = useState([]);

  // ordine cronologico unico per post + discussion nella griglia
  const gridOrder = useMemo(() => {
    const items = [
      ...userDiscussions.map((d) => ({ id: `d_${d.id}`, ts: d.created_at || "" })),
      ...(user?.photos || []).map((p) => ({ id: `p_${p.photo_id}`, ts: p.created_at || "" })),
    ].sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    const m = {};
    items.forEach((it, i) => { m[it.id] = i; });
    return m;
  }, [userDiscussions, user?.photos]);
  useEffect(() => {
    if (!userId) return;
    const load = () => api.get(`/discussions/user/${userId}`).then((d) => setUserDiscussions(d.data.discussions)).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [userId]);
  const [processing, setProcessing] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [photoComments, setPhotoComments] = useState({});
  const [submittingComment, setSubmittingComment] = useState(false);
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const [zoomIndex, setZoomIndex] = useState(0);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showConnectionsDialog, setShowConnectionsDialog] = useState(false);
  const [showGiftsDialog, setShowGiftsDialog] = useState(false);
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get(`/profile/${userId}`);
        setUser(response.data);
      } catch (error) {
        toast.error("Profilo non trovato");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [userId, navigate]);
  
  const handleConnect = async () => {
    setProcessing(true);
    try {
      playConnectionSound('send');
      const response = await api.post(`/connections/request/${userId}`);
      
      if (response.data.status === 'connected') {
        playConnectionSound('accept');
        setUser(prev => ({ ...prev, is_connected: true, has_pending_request: false }));
        toast.success("Ora siete collegati!");
      } else {
        setUser(prev => ({ ...prev, has_pending_request: true, request_direction: 'sent' }));
        toast.success("Richiesta inviata!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    } finally {
      setProcessing(false);
    }
  };
  
  const handleBlock = async () => {
    try {
      await api.post(`/users/${userId}/block`);
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };
  
  const REPORT_REASONS = [
    "Contenuto inappropriato",
    "Molestie o bullismo",
    "Spam o truffa",
    "Profilo falso",
    "Incitamento all'odio",
    "Altro",
  ];
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  
  const handleReport = async () => {
    if (!selectedReason) {
      toast.error("Seleziona un motivo");
      return;
    }
    try {
      await api.post(`/users/${userId}/report`, { reason: selectedReason });
      toast.success("Segnalazione inviata");
      setShowReportModal(false);
      setSelectedReason("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };
  
  const handleRemoveConnection = async () => {
    setProcessing(true);
    try {
      await api.delete(`/connections/${userId}`);
      playConnectionSound('cancel');
      setUser(prev => ({ ...prev, is_connected: false, has_pending_request: false }));
      toast.success("Collegamento rimosso");
      setShowRemoveDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella rimozione");
    } finally {
      setProcessing(false);
    }
  };
  
  const handleLikePhoto = async (photoId) => {
    if (!user?.is_connected) {
      toast.info("Collegati per mettere AdE Like");
      return;
    }
    
    try {
      const response = await api.post(`/photos/${photoId}/like`);
      
      setUser(prev => ({
        ...prev,
        photos: prev.photos.map(p => 
          p.photo_id === photoId 
            ? { ...p, is_liked: response.data.liked, likes_count: (p.likes_count || 0) + (response.data.liked ? 1 : -1) }
            : p
        )
      }));
      
      if (selectedPhoto?.photo_id === photoId) {
        setSelectedPhoto(prev => ({
          ...prev,
          is_liked: response.data.liked,
          likes_count: (prev.likes_count || 0) + (response.data.liked ? 1 : -1)
        }));
      }
    } catch (error) {
      toast.error("Errore nel like");
    }
  };
  
  const fetchPhotoComments = async (photoId) => {
    try {
      const response = await api.get(`/photos/${photoId}/comments`);
      setPhotoComments(prev => ({ ...prev, [photoId]: response.data.comments || [] }));
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };
  
  const handleSubmitComment = async (photoId) => {
    if (!commentText.trim() || !user?.is_connected) return;
    
    setSubmittingComment(true);
    try {
      await api.post(`/photos/${photoId}/comment`, { text: commentText });
      setCommentText("");
      fetchPhotoComments(photoId);
      toast.success("Commento aggiunto!");
    } catch (error) {
      toast.error("Errore nel commento");
    } finally {
      setSubmittingComment(false);
    }
  };
  
  const openPhotoDetail = (photo, index) => {
    if (user?.is_connected) {
      // Connected: open interaction modal
      setSelectedPhoto(photo);
      fetchPhotoComments(photo.photo_id);
    } else if (user?.is_public !== false) {
      // Public profile, not connected: open zoom only
      setZoomIndex(index);
      setZoomPhoto(photo);
    }
  };
  
  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };
  
  // Format gift value
  const formatCoins = (amount) => {
    if (!amount) return '0';
    if (amount >= 1000000) {
      const m = amount / 1000000;
      return m % 1 === 0 ? `${m.toFixed(0)}M` : `${m.toFixed(1)}M`;
    }
    if (amount >= 1000) {
      const k = amount / 1000;
      return k % 1 === 0 ? `${k.toFixed(0)}K` : `${k.toFixed(1)}K`;
    }
    return amount.toString();
  };
  
  // Fetch user's connections
  const handleShowConnections = async () => {
    setShowConnectionsDialog(true);
    setLoadingConnections(true);
    try {
      const response = await api.get(`/profile/${userId}/connections`);
      setConnections(response.data.connections || []);
    } catch (error) {
      console.error('Error fetching connections:', error);
      setConnections([]);
    } finally {
      setLoadingConnections(false);
    }
  };
  
  // Vetrina trofei: apre la modale con i gift ricevuti (dati da /gifts/received/{userId})
  const handleShowGifts = () => setShowGiftsDialog(true);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-orange-50/30">
        <AdeLoadingSpinner size="lg" />
      </div>
    );
  }
  
  // Check if profile is viewable (public or connected)
  const canViewPhotos = user?.is_connected || user?.is_public !== false;
  const canInteract = user?.is_connected;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/20 pb-24" data-testid="user-profile-page">
      {/* Header */}
      <div className="sticky top-0 safe-top z-50 bg-white/80 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" strokeWidth={2} />
          </button>
          
          <span className="font-semibold text-gray-800">{user?.name}</span>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 -mr-2 rounded-full hover:bg-gray-100 transition-colors">
                <MoreHorizontal className="w-5 h-5 text-gray-700" strokeWidth={2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => setShowReportModal(true)}>Segnala</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleBlock} className="text-red-500">Blocca</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Equipped island banner */}
      <div className="relative h-48" data-testid="profile-island-banner">
        <img src={getSceneImage(user?.island_type, false)} alt="" draggable={false}
             className="w-full h-full object-cover" style={{ objectPosition: "center 52%" }} />
        <div className="absolute inset-0 island-banner-fade" />
        {user?.island_name && (
          <button
            onClick={() => {
              const name = user.island_name;
              const done = () => toast.success("Nome isola copiato!");
              const fallback = () => {
                const ta = document.createElement("textarea");
                ta.value = name; document.body.appendChild(ta); ta.select();
                try { document.execCommand("copy"); done(); } catch {}
                document.body.removeChild(ta);
              };
              if (navigator.clipboard?.writeText) navigator.clipboard.writeText(name).then(done).catch(fallback);
              else fallback();
            }}
            className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-semibold text-white flex items-center gap-1 active:scale-95 transition-transform"
            style={{ background: "rgba(8,12,24,0.5)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.15)" }}
            data-testid="island-name-chip"
          >
            <Palmtree className="w-3 h-3 text-emerald-300" />
            {user.island_name}
            <Copy className="w-2.5 h-2.5 text-white/60 ml-0.5" />
          </button>
        )}
      </div>

      {/* Profile Section — compact left-avatar layout (same as own profile) */}
      <div className="px-4 relative z-10 -mt-10">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div
            className="relative flex-shrink-0 cursor-pointer group"
            onClick={() => {
              if (user?.picture) {
                setZoomPhoto({ image_url: user.picture });
              }
            }}
            data-testid="avatar-zoom-trigger"
          >
            <GoldenRingAvatar
              user={user}
              size="lg"
              onClick={() => {
                if (user?.picture) {
                  setZoomPhoto({ image_url: user.picture });
                }
              }}
            />
            <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
              <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>

          {/* Stats — identical to own profile */}
          <div className="flex-1 flex justify-around pt-2">
            <div className="text-center" data-testid="post-count">
              <div className="text-xl font-semibold text-gray-900">{user?.photos?.length || 0}</div>
              <div className="text-xs text-gray-500">{t('post')}</div>
            </div>
            <div
              className="text-center cursor-pointer"
              onClick={handleShowConnections}
              data-testid="connections-btn"
            >
              <div className="text-xl font-semibold text-gray-900">{user?.connections_count || 0}</div>
              <div className="text-xs text-gray-500">{t('connectionsTxt')}</div>
            </div>
            <div className="text-center" data-testid="likes-count">
              <div className="text-xl font-semibold text-gray-900">{user?.total_likes_received || 0}</div>
              <div className="text-xs text-gray-500">{t('adeLikes')}</div>
            </div>
            {user?.gifts_visible !== false && (
            <div
              className="text-center cursor-pointer active:scale-95 transition-transform"
              onClick={handleShowGifts}
              data-testid="gifts-btn"
            >
              <div className="text-xl font-semibold" style={{ color: "#D4AF37" }}>
                {formatCoins(user?.total_gifts_received || 0)}
              </div>
              <div className="text-xs font-semibold" style={{ color: "#D4AF37" }}>Regali</div>
              {(user?.total_gift_value || 0) > 0 && (
                <div className="text-[10px] text-amber-500 font-semibold">{formatCoins(user.total_gift_value)} ADE</div>
              )}
            </div>
            )}
          </div>
        </div>

        {/* Name & Bio */}
        <div className="mt-4">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 flex-wrap">
            {user?.name}
            {user?.age && <span className="text-gray-400 font-normal">{user.age}</span>}
            <CreatorBadge user={user} />
            <CreatorLevelBadge
              level={user?.creator_level ? { level: user.creator_level.level, name: user.creator_level.name } : null}
            />
          </h2>
          {user?.city && (
            <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
              <MapPin className="w-3 h-3" strokeWidth={2.2} />
              {user.city}
            </p>
          )}
          {user?.bio && (
            <p className="text-sm text-gray-700 mt-2 leading-relaxed">{user.bio}</p>
          )}
        </div>

        
        {/* Action Buttons */}
        <div className="flex flex-col gap-3 mt-4">
          {user?.is_connected ? (
            <>
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate(`/app/chat/${userId}`)}
                  className="flex-1 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white rounded-xl shadow-lg shadow-pink-200/50 h-12"
                  data-testid="message-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Messaggio
                </Button>
                <div className="h-12 flex items-center px-4 rounded-xl border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors">
                  <GiftButton
                    recipientId={userId}
                    totalGifts={user?.total_gifts_received || 0}
                    size="md"
                    onGiftSent={(amount) => {
                      setUser(prev => ({
                        ...prev,
                        total_gifts_received: (prev.total_gifts_received || 0) + amount
                      }));
                    }}
                  />
                </div>
              </div>
              {/* Remove connection button */}
              <Button
                onClick={() => setShowRemoveDialog(true)}
                variant="outline"
                className="w-full h-10 rounded-xl border-red-200 hover:bg-red-50 text-red-500 hover:text-red-600"
                data-testid="remove-connection-btn"
              >
                <UserMinus className="w-4 h-4 mr-2" />
                Rimuovi collegamento
              </Button>
            </>
          ) : !user?.has_pending_request ? (
            <Button
              onClick={handleConnect}
              disabled={processing}
              className="flex-1 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white rounded-xl shadow-lg shadow-pink-200/50 h-12 text-base"
              data-testid="connect-btn"
            >
              {processing ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Invio...
                </span>
              ) : (
                user?.profile_type === 'professional' ? 'Segui' : 'Richiedi Collegamento'
              )}
            </Button>
          ) : (
            <div className="flex-1 flex items-center justify-center gap-3 py-3 bg-gradient-to-r from-orange-50 to-pink-50 rounded-xl border border-orange-200">
              {/* Luminous bar for pending */}
              <div className="relative w-24 h-2 rounded-full overflow-hidden bg-gray-200">
                <div className="absolute inset-y-0 w-10 bg-gradient-to-r from-transparent via-orange-400 to-transparent animate-scan-bar rounded-full" />
              </div>
              <span className="text-sm text-orange-600 font-medium">
                {user.request_direction === 'sent' ? 'Richiesta inviata' : 'Richiesta ricevuta'}
              </span>
            </div>
          )}
        </div>
      </div>
      
      {/* Photo Grid — Discussions live in the same grid as Posts */}
      {((user?.photos && user.photos.length > 0) || userDiscussions.length > 0) && (
        <div className="mt-6 border-t border-gray-100">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2 text-gray-900">
              <Grid3X3 className="w-4 h-4" strokeWidth={2} />
              <span className="text-sm font-medium">Post</span>
            </div>
            {!canInteract && canViewPhotos && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Eye className="w-3 h-3" />
                Solo visualizzazione
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-3 gap-0.5" data-testid="profile-discussions">
            {userDiscussions.map((d) => <DiscussionGridTile key={d.id} d={d} style={{ order: gridOrder[`d_${d.id}`] ?? 0 }} />)}
            {user.photos.map((photo, index) => (
              <div 
                key={photo.photo_id} 
                className="aspect-square bg-gray-100 relative group cursor-pointer"
                style={{ order: gridOrder[`p_${photo.photo_id}`] ?? 0 }}
                onClick={() => openPhotoDetail(photo, index)}
              >
                {photo.media_type === 'video' ? (
                  <>
                    <video
                      src={`${getImageUrl(photo.image_url)}#t=0.001`}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                      data-testid={`grid-video-${photo.photo_id}`}
                    />
                    <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center pointer-events-none z-10">
                      <Play className="w-3 h-3 text-white" fill="white" />
                    </div>
                  </>
                ) : (
                  <img 
                    src={getImageUrl(photo.image_url)}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
                
                {/* Stats overlay - ALWAYS visible (even for public non-connected) */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                    <div className="flex items-center gap-1 text-white text-xs">
                      <img src="/images/ade-logo-extracted.png" alt="" className="w-4 h-4 object-contain" />
                      <span className="font-semibold">{photo.likes_count || 0}</span>
                    </div>
                    {photo.total_gifts > 0 && (
                      <div className="flex items-center gap-1 text-amber-300 text-xs">
                        <Gift className="w-3.5 h-3.5" />
                        <span className="font-semibold">{formatCoins(photo.total_gifts)}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Lock overlay for private non-connected */}
                {!canViewPhotos && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Lock className="w-6 h-6 text-white/70" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Empty state */}
      {(!user?.photos || user.photos.length === 0) && (
        <div className="mt-8 text-center text-gray-400">
          <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
            <img src="/images/ade-logo-extracted.png" alt="" className="w-8 h-8 opacity-30 object-contain" />
          </div>
          <p className="text-sm">{t("noPostsYet")}</p>
        </div>
      )}
      
      {/* Photo Detail Modal - For CONNECTED users only */}
      <Dialog open={!!selectedPhoto} onOpenChange={(open) => !open && setSelectedPhoto(null)}>
        <DialogContent className="max-w-lg mx-auto p-0 rounded-2xl max-h-[88dvh] overflow-y-auto overscroll-contain" data-testid="post-detail-modal">
          {selectedPhoto && (
            <div className="flex flex-col">
              {/* Header */}
              <DialogHeader className="p-3 border-b flex-row items-center gap-3 sticky top-0 z-10 bg-background">
                <GoldenRingAvatar user={user} size="sm" />
                <DialogTitle className="flex-1 text-left text-sm font-semibold">{user?.name}</DialogTitle>
                <button onClick={() => setSelectedPhoto(null)} className="p-1" data-testid="post-detail-close">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </DialogHeader>
              
              {/* Photo with zoom on click */}
              <div 
                className={`relative bg-black ${selectedPhoto.media_type === 'video' ? '' : 'cursor-zoom-in'}`}
                onClick={selectedPhoto.media_type === 'video' ? undefined : () => {
                  const index = user?.photos?.findIndex(p => p.photo_id === selectedPhoto.photo_id) || 0;
                  setZoomIndex(index);
                  setZoomPhoto(selectedPhoto);
                }}
              >
                {selectedPhoto.media_type === 'video' ? (
                  <video
                    src={getImageUrl(selectedPhoto.image_url)}
                    className="w-full max-h-[40vh] object-contain"
                    controls
                    playsInline
                    autoPlay
                    muted
                    data-testid="photo-detail-video"
                  />
                ) : (
                  <img 
                    src={getImageUrl(selectedPhoto.image_url)}
                    alt=""
                    className="w-full max-h-[40vh] object-contain"
                  />
                )}
                {selectedPhoto.media_type !== 'video' && (
                <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-black/50 text-white text-xs flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  Clicca per ingrandire
                </div>
                )}
              </div>
              
              {/* Actions - AdE Like, Gift, Comment */}
              <div className="p-3 border-t overflow-y-auto max-h-[35vh]">
                <div className="flex items-center gap-4 mb-3">
                  {/* AdE Like - LARGE 48px */}
                  <AdLikeIcon
                    filled={selectedPhoto.is_liked}
                    size={48}
                    onClick={() => handleLikePhoto(selectedPhoto.photo_id)}
                  />
                  
                  {/* Gift Button */}
                  <GiftButton
                    photoId={selectedPhoto.photo_id}
                    recipientId={userId}
                    totalGifts={selectedPhoto.total_gifts || 0}
                    onGiftSent={(amount) => {
                      setSelectedPhoto(prev => ({
                        ...prev,
                        total_gifts: (prev.total_gifts || 0) + amount
                      }));
                      setUser(prev => ({
                        ...prev,
                        photos: prev.photos.map(p =>
                          p.photo_id === selectedPhoto.photo_id
                            ? { ...p, total_gifts: (p.total_gifts || 0) + amount }
                            : p
                        )
                      }));
                    }}
                  />
                  
                  <div className="flex-1" />
                  
                  {/* Gift counter */}
                  {selectedPhoto.total_gifts > 0 && (
                    <GiftValueCounter totalValue={selectedPhoto.total_gifts} />
                  )}
                </div>
                
                {/* Likes count */}
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  {selectedPhoto.likes_count || 0} AdE Like
                </p>
                
                {/* Caption */}
                {selectedPhoto.caption && (
                  <p className="text-sm text-gray-700 mb-3">
                    <span className="font-semibold">{user?.name}</span> {selectedPhoto.caption}
                  </p>
                )}
                
                {/* Comments */}
                <div className="max-h-24 overflow-y-auto space-y-2 mb-3">
                  {(photoComments[selectedPhoto.photo_id] || []).map((comment) => (
                    <div key={comment.comment_id} className="flex gap-2 text-sm">
                      <span className="font-semibold text-gray-900">{comment.user?.name}</span>
                      <span className="text-gray-700">{comment.text}</span>
                    </div>
                  ))}
                </div>
                
                {/* Add comment */}
                <div className="relative flex gap-2">
                  <MentionSuggestions
                    text={commentText}
                    onPick={(u) => setCommentText((prev) => applyMention(prev, u.username))}
                  />
                  <Input
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Aggiungi un commento..."
                    className="flex-1 rounded-full bg-gray-100 border-0"
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment(selectedPhoto.photo_id)}
                  />
                  <button
                    onClick={() => handleSubmitComment(selectedPhoto.photo_id)}
                    disabled={!commentText.trim() || submittingComment}
                    className="p-2.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Photo Zoom Dialog */}
      <PhotoZoomDialog
        isOpen={!!zoomPhoto}
        onClose={() => setZoomPhoto(null)}
        photos={zoomPhoto ? [zoomPhoto] : []}
        initialIndex={0}
        showNavigation={false}
      />
      
      {/* CSS */}
      <style>{`
        @keyframes scan-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .animate-scan-bar {
          animation: scan-bar 1.5s ease-in-out infinite;
        }
      `}</style>
      
      {/* Remove Connection Dialog */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <UserMinus className="w-5 h-5 text-red-500" />
              Rimuovi collegamento
            </AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler rimuovere il collegamento con <span className="font-semibold">{user?.name}</span>? 
              Non potrete più scambiarvi messaggi e regali.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveConnection}
              className="bg-red-500 hover:bg-red-600 text-white rounded-xl"
              disabled={processing}
            >
              {processing ? "Rimozione..." : "Rimuovi"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Connections Dialog */}
      <Dialog open={showConnectionsDialog} onOpenChange={setShowConnectionsDialog}>
        <DialogContent className="max-w-md mx-auto max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              Collegamenti di {user?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] -mx-4 px-4">
            {loadingConnections ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
              </div>
            ) : connections.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>{t("noConnectionsShort")}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {connections.map((conn) => (
                  <div 
                    key={conn.user_id}
                    onClick={() => {
                      setShowConnectionsDialog(false);
                      navigate(`/app/user/${conn.user_id}`);
                    }}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <GoldenRingAvatar user={conn} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{conn.name}</p>
                      {conn.city && <p className="text-xs text-gray-500 truncate">{conn.city}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Vetrina trofei — gift ricevuti */}
      <GiftDetailsModal userId={userId} open={showGiftsDialog} onOpenChange={setShowGiftsDialog} />

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="sm:max-w-sm">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{t("reportUser")}</h3>
              <p className="text-xs text-gray-500 mt-1">{t("reportReason")}</p>
            </div>
            <div className="space-y-2">
              {REPORT_REASONS.map((reason) => (
                <button
                  key={reason}
                  onClick={() => setSelectedReason(reason)}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${
                    selectedReason === reason
                      ? "border-red-400 bg-red-50 text-red-700 font-medium"
                      : "border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                  data-testid={`report-reason-${reason.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {reason}
                </button>
              ))}
            </div>
            <Button
              onClick={handleReport}
              disabled={!selectedReason}
              className="w-full bg-red-500 hover:bg-red-600 text-white"
              data-testid="submit-report-btn"
            >
              Invia segnalazione
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
